import {
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  IconIdentifier,
  toast,
} from '@aus-platform/design-system';
import { isEmpty, lt } from 'lodash';
import React, { useEffect, useState } from 'react';
import { BackupCodes, SectionHeader } from 'shared/components';
import { useRegenerateBackupCodes } from 'shared/api';
import {
  BACKUP_CODES_CONFIG,
  BACKUP_CODES_MESSAGES,
  LOW_CODES_THRESHOLD,
} from '../constants';
import { SetupFlowMode } from '../enums';
import type { BackupCodesViewProps } from '../types';

export const BackupCodesView: React.FC<BackupCodesViewProps> = ({
  codes: initialCodes,
  mode,
  elevatedToken,
  onBack,
  onFinish,
}) => {
  // States.
  const [codes, setCodes] = useState<string[]>(initialCodes);
  const [showSaveWarning, setShowSaveWarning] = useState(false);

  // Variables.
  const hasNoCodes = isEmpty(codes);
  const hasLowCodes = lt(codes.length, LOW_CODES_THRESHOLD);
  const backupCodesWarningMessage = hasLowCodes
    ? `You have ${codes.length} unused backup code${codes.length === 1 ? '' : 's'} remaining. Generate a new set of backup codes to avoid losing access to your account.`
    : BACKUP_CODES_MESSAGES.SaveWarning;

  const isConfigureMode = mode === SetupFlowMode.Configure;
  const { backLabel, description, footerButtonLabel } =
    BACKUP_CODES_CONFIG[mode];

  // Apis.
  const {
    mutate: regenerateCodes,
    isPending: isRegenerating,
    isSuccess: isRegenerateSuccess,
    isError: isRegenerateError,
    data: regenerateResponse,
  } = useRegenerateBackupCodes();

  // UseEffects.
  useEffect(() => {
    if (isRegenerateSuccess) {
      setCodes(regenerateResponse.data.backupCodes);
      setShowSaveWarning(true);
      toast.success(BACKUP_CODES_MESSAGES.RegenerateSuccess);
    }

    if (isRegenerateError) {
      toast.error(BACKUP_CODES_MESSAGES.RegenerateError);
    }
  }, [isRegenerateSuccess, isRegenerateError]);

  const handleContinue = () => {
    if (isConfigureMode) {
      onFinish();
    } else {
      regenerateCodes(elevatedToken);
    }
  };

  return (
    <div className="settings-content">
      <div className="settings-content__body backup-codes-view">
        <SectionHeader
          backLabel={backLabel}
          description={description}
          onBack={onBack}
        />

        {hasNoCodes ? (
          <FormMessage
            variant={FormMessageVariant.Warning}
            message={BACKUP_CODES_MESSAGES.NoCodes}
            showIcon
          />
        ) : (
          <>
            <div className="backup-codes-view__content">
              {!isConfigureMode && (
                <div className="backup-codes-view__regenerate">
                  <Button
                    variant={ButtonVariant.Link}
                    leftIconIdentifier={IconIdentifier.Loading}
                    iconSize={14}
                    onClick={() => regenerateCodes(elevatedToken)}
                    isLoading={isRegenerating}
                  >
                    {isRegenerating ? 'Regenerating' : 'Regenerate'}
                  </Button>
                </div>
              )}

              <BackupCodes codes={codes} />
            </div>

            {(hasLowCodes || showSaveWarning) && (
              <FormMessage
                variant={FormMessageVariant.Warning}
                message={backupCodesWarningMessage}
                showIcon
              />
            )}
          </>
        )}
      </div>

      {(hasNoCodes || isConfigureMode) && (
        <div className="settings-content__footer">
          <Button onClick={handleContinue} isLoading={isRegenerating}>
            {footerButtonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
